import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Card, PageHeader, Spinner, EmptyState, StatusBadge, Button, fmtEur, Pill } from "@/components/common";
import { UserCircle, IdCard, HeartPulse, Euro, ClipboardCheck, CreditCard, Check } from "lucide-react";
import { PayPalPay } from "@/components/PayPalPay";

function StatusRow({ icon: Icon, label, status, extra }) {
  return (
    <div className="flex items-center justify-between py-2.5 border-b border-slate-100 last:border-0">
      <span className="flex items-center gap-2.5 text-sm text-slate-600"><Icon size={17} className="text-slate-400" /> {label}</span>
      {status ? <StatusBadge status={status} /> : <span className="text-sm font-semibold text-slate-700">{extra}</span>}
    </div>
  );
}

export default function Portale() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [pp, setPp] = useState(null);

  const load = () => api.get("/portale").then((r) => setData(r.data)).catch((e) => { toast.error(apiErr(e)); setData({ membri: [] }); });
  useEffect(() => { load(); }, []);

  const payOnline = (pg, r) => setPp({ kind: "rata", pagamento_id: pg.id, rata_numero: r.numero, label: `${pg.descrizione} — Rata ${r.numero}`, amount: r.importo });

  if (!data) return <Spinner />;
  if (!data.membri.length) return (
    <div><PageHeader title={`Benvenuto, ${user?.name?.split(" ")[0]}`} /><Card className="p-6"><EmptyState icon={UserCircle} title="Nessun profilo collegato" hint="Contatta la segreteria per collegare il tuo profilo atleta." /></Card></div>
  );

  return (
    <div>
      <PageHeader title={`Benvenuto, ${user?.name?.split(" ")[0]} 👋`} subtitle="Il tuo portale personale" />
      <div className="grid lg:grid-cols-2 gap-5 max-w-4xl">
        {data.membri.map((m) => {
          const p = m.persona;
          return (
            <Card key={p.id} className="p-6" data-testid={`portale-membro-${p.id}`}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-sky-100 grid place-items-center"><UserCircle size={30} className="text-sky-600" /></div>
                <div><h3 className="font-bold font-display text-lg text-slate-900">{p.cognome} {p.nome}</h3><p className="text-xs text-slate-400">{(p.ruoli || []).join(" · ")}</p></div>
              </div>
              <div className="mb-4">
                <StatusRow icon={IdCard} label="Tesseramento" status={m.tesseramento.status || "orange"} />
                <StatusRow icon={HeartPulse} label="Certificato medico" status={m.certificato.status || "orange"} />
                <StatusRow icon={ClipboardCheck} label="Presenze" extra={`${m.presenze_rate}%`} />
              </div>
              {m.pagamenti.map((pg) => (
                <div key={pg.id} className="rounded-xl border border-slate-200 p-4 mt-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold text-slate-800 flex items-center gap-1.5"><Euro size={15} /> {pg.descrizione}</span>
                    <Pill className={pg.stato === "saldato" ? "bg-emerald-100 text-emerald-700" : pg.stato === "parziale" ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}>{pg.stato}</Pill>
                  </div>
                  <div className="space-y-1.5">
                    {pg.rate.map((r) => (
                      <div key={r.numero} className="flex items-center justify-between text-sm">
                        <span className="text-slate-500">Rata {r.numero} · {fmtEur(r.importo)}</span>
                        {r.pagata ? <span className="text-emerald-600 text-xs font-semibold flex items-center gap-1"><Check size={13} /> Pagata</span>
                          : <Button size="sm" variant="accent" onClick={() => payOnline(pg, r)} data-testid={`portale-pay-${pg.id}-${r.numero}`}><CreditCard size={13} /> Paga</Button>}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </Card>
          );
        })}
      </div>
      <PayPalPay open={!!pp} order={pp || {}} onClose={() => setPp(null)} onSuccess={load} />
    </div>
  );
}
